import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OverallReport from "./OverallReport";
import SummaryReport from "./SummaryReport";
import UpgradationReport from "./UpgradationReport";
import NewShuttlelessReport from "./NewShuttlelessReport";
import CFCReport from "./CFCReport";
import DashboardReport from "./DashboardReport";

const toDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const getDateRange = (filter) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case "overall":
      return { from: "", to: "" };

    case "today":
      return { from: toDateStr(today), to: toDateStr(today) };

    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { from: toDateStr(yesterday), to: toDateStr(yesterday) };
    }

    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "previous_month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "previous_year": {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "current_financial_year": {
      const fyStartYear =
        today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      const start = new Date(fyStartYear, 3, 1);
      const end = new Date(fyStartYear + 1, 2, 31);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "previous_financial_year": {
      const fyStartYear =
        today.getMonth() >= 3
          ? today.getFullYear() - 1
          : today.getFullYear() - 2;
      const start = new Date(fyStartYear, 3, 1);
      const end = new Date(fyStartYear + 1, 2, 31);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    default:
      return { from: "", to: "" };
  }
};

const buildFilterOptions = () => {
  const now = new Date();
  const curFyStart =
    now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const prevFyStart = curFyStart - 1;
  const sy = (y) => String(y).slice(2);

  return [
    { value: "overall", label: "All" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "this_month", label: "This Month" },
    { value: "previous_month", label: "Previous Month" },
    { value: "previous_year", label: "Previous Year" },
    {
      value: "current_financial_year",
      label: `Current Financial Year (Apr ${sy(curFyStart)} – Mar ${sy(curFyStart + 1)})`,
    },
    {
      value: "previous_financial_year",
      label: `Previous Financial Year (Apr ${sy(prevFyStart)} – Mar ${sy(prevFyStart + 1)})`,
    },
  ];
};

const FILTER_OPTIONS = buildFilterOptions();

const Report = () => {
  const [currentTab, setCurrentTab] = useState("overall");
  const [selectedFilter, setSelectedFilter] = useState("overall");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    setDateRange(getDateRange(value));
  };

  const filterLabel =
    selectedFilter !== "overall" && dateRange.from && dateRange.to
      ? `${formatDisplayDate(dateRange.from)} to ${formatDisplayDate(dateRange.to)}`
      : null;

  const reportProps = {
    fromDate: dateRange.from,
    toDate: dateRange.to,
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl leading-tight">
              Report Details
            </CardTitle>

            <div className="flex items-center gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Filter By Date Range
                </label>
                {filterLabel && (
                  <p className="text-xs p-2 text-muted-foreground hidden sm:block">
                    {filterLabel}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1"> 
                <Select
                  value={selectedFilter}
                  onValueChange={handleFilterChange}
                >
                  <SelectTrigger className="w-[260px]">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4">
          <Tabs
            value={currentTab}
            onValueChange={setCurrentTab}
            className="w-full"
          >
            <TabsList className="w-full grid grid-cols-5">
              <TabsTrigger value="overall">Overall Report</TabsTrigger>
              <TabsTrigger value="summary">Summary Report</TabsTrigger>
              <TabsTrigger value="dashboard_report">
                Dashboard Report
              </TabsTrigger>
              <TabsTrigger value="upgradation">Upgradation Scheme</TabsTrigger>
              <TabsTrigger value="new_shuttleless">New Shuttleless</TabsTrigger>
              <TabsTrigger value="cfc">CFC Scheme</TabsTrigger>
            </TabsList>

            <TabsContent value="overall" className="mt-4">
              <OverallReport {...reportProps} />
            </TabsContent>
            <TabsContent value="summary" className="mt-4">
              <SummaryReport {...reportProps} />
            </TabsContent>
            <TabsContent value="dashboard_report" className="mt-4">
              <DashboardReport {...reportProps} />
            </TabsContent>
            <TabsContent value="upgradation" className="mt-4">
              <UpgradationReport {...reportProps} />
            </TabsContent>
            <TabsContent value="new_shuttleless" className="mt-4">
              <NewShuttlelessReport {...reportProps} />
            </TabsContent>
            <TabsContent value="cfc" className="mt-4">
              <CFCReport {...reportProps} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
};

export default Report;
